import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Instagram,
  Facebook,
  Mail,
  ArrowUp,
  ArrowUpRight,
  FileText,
} from "lucide-react";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

export default function Footer() {
  const { t } = useTranslation();
  const year = useMemo(() => new Date().getFullYear(), []);

  const backToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-brand-brown text-brand-ivory">
      {/* subtle wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-brown via-brand-brown to-brand-brown" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-16 pt-14 pb-10">
        {/* TOP */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          {/* brand */}
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.62em] text-brand-ivory/70">
              OWL DREAM
            </p>
            <p className="text-sm text-brand-ivory/60">
              {t("footer.tagline")}
            </p>
          </div>

          {/* nav */}
          <nav className="flex flex-wrap items-center gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.55em] text-brand-ivory/60">
            <Link to="/collection" className="hover:text-brand-gold transition">
              {t("nav.shop")}
            </Link>
            <Link to="/drops" className="hover:text-brand-gold transition">
              {t("nav.drops")}
            </Link>
            {/* <Link to="/lookbook" className="hover:text-brand-gold transition">
              {t("nav.lookbook")}
            </Link> */}

            {/* Policies */}
            <Link to="/policies" className="hover:text-brand-gold transition">
              {t("nav.policies")}
            </Link>

            {/* small CTA */}
            <Link
              to="/collection"
              className="inline-flex items-center gap-2 rounded-full border border-brand-ivory/15 bg-brand-ivory/5
                         px-4 py-2 text-brand-ivory/70 hover:border-brand-gold/60 hover:text-brand-gold transition"
              aria-label={t("footer.newInAria")}
              title={t("footer.newInTitle")}
            >
              {t("footer.newIn")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>

        {/* LEGAL QUICK LINKS */}
        <div className="mt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.55em] text-brand-ivory/40 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t("footer.legal")}
            </p>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.55em] text-brand-ivory/55">
              <Link
                to="/policies#shipping"
                className="hover:text-brand-gold transition"
              >
                {t("footer.shipping")}
              </Link>
              <Link
                to="/policies#payment"
                className="hover:text-brand-gold transition"
              >
                {t("footer.payment")}
              </Link>
              <Link
                to="/policies#returns"
                className="hover:text-brand-gold transition"
              >
                {t("footer.returns")}
              </Link>
              <Link
                to="/policies#privacy"
                className="hover:text-brand-gold transition"
              >
                {t("footer.privacy")}
              </Link>
              <Link
                to="/policies#terms"
                className="hover:text-brand-gold transition"
              >
                {t("footer.terms")}
              </Link>
            </div>
          </div>
        </div>

        {/* DIVIDER */}
        <div className="mt-10 h-px bg-brand-ivory/10" />

        {/* BOTTOM */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* left */}
          <p className="text-[10px] uppercase tracking-[0.55em] text-brand-ivory/40">
            © {year} OWL DREAM
          </p>

          {/* right */}
          <div className="flex items-center gap-3">
            {/* socials */}
            <a
              href="https://www.instagram.com/owl_dream25"
              target="_blank"
              rel="noreferrer"
              className="h-10 w-10 rounded-full border border-brand-ivory/15 bg-brand-ivory/5
                         grid place-items-center hover:border-brand-gold/60 hover:text-brand-gold transition"
              aria-label={t("footer.instagramAria")}
              title={t("footer.instagramTitle")}
            >
              <Instagram className="w-4 h-4" />
            </a>

            <a
              href="https://www.facebook.com/owldream25"
              target="_blank"
              rel="noreferrer"
              className="h-10 w-10 rounded-full border border-brand-ivory/15 bg-brand-ivory/5
                         grid place-items-center hover:border-brand-gold/60 hover:text-brand-gold transition"
              aria-label={t("footer.facebookAria")}
              title={t("footer.facebookTitle")}
            >
              <Facebook className="w-4 h-4" />
            </a>

            <a
              href="mailto:contact@owldream.com"
              className="h-10 w-10 rounded-full border border-brand-ivory/15 bg-brand-ivory/5
                         grid place-items-center hover:border-brand-gold/60 hover:text-brand-gold transition"
              aria-label={t("footer.emailAria")}
              title={t("footer.emailTitle")}
            >
              <Mail className="w-4 h-4" />
            </a>

            {/* back to top */}
            <button
              type="button"
              onClick={backToTop}
              className={cn(
                "ml-2 inline-flex items-center gap-2",
                "h-10 px-4 rounded-full border border-brand-ivory/15 bg-brand-ivory/5",
                "text-[10px] uppercase tracking-[0.55em] text-brand-ivory/60",
                "hover:border-brand-gold/60 hover:text-brand-gold transition",
              )}
              aria-label={t("footer.backToTopAria")}
              title={t("footer.backToTopTitle")}
            >
              {t("footer.top")} <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
